import { useRef } from 'react'
import { useRouter } from 'next/router'
import NavigationTabs from '@/components/NavigationTabs'
import useHiddenHeader from '@/hooks/useHiddenHeader'
import useIsSticky from '@/hooks/useIsSticky'
import { ARCADE_LINKS } from '../constants'
import { Season } from '@/features/arcade/types'
import { canUpdateArcadeSeason } from '@/config'
import { Button, Icon } from '@/components'
import { showModal } from '@/components/Modals/ModalV2'
import { MODAL_ID } from '@/constants/modalId'
import { classNames } from '@/util/tailwindHelpers'

type Props = {
  season: Season
  className?: string
  style?: React.CSSProperties
  hasConsistentBottomBorder?: boolean
  isTransparent?: boolean
  seasonId?: number
}
const ArcadeNavigation = ({
  season,
  hasConsistentBottomBorder = true,
  className,
  style,
  isTransparent = false,
  seasonId = null
}: Props) => {
  const { pathname } = useRouter()
  const navRef = useRef<HTMLDivElement>()
  const isSticky = useIsSticky(navRef, {
    threshold: 0.999,
    rootMargin: '-1px 0px -0px 0px'
  })
  useHiddenHeader(isSticky)

  const subtitle = !!season ? `Arcade ${season.name}` : 'Arcade'

  let arcadeLinks = ARCADE_LINKS
  if (seasonId) {
    arcadeLinks = ARCADE_LINKS.map(link => {
      if (link.label === 'Leaderboard' && seasonId != null) {
        // Avoid duplicating ?season=
        const alreadyHasParam = link.path.includes('season=')
        const basePath = link.path.split('?')[0]
        return {
          ...link,
          path: alreadyHasParam ? link.path : `${basePath}?season=${seasonId}`
        }
      }
      return link
    })
  }

  return (
    <div
      ref={navRef}
      style={style}
      className={classNames(
        className,
        { 'before:border-b': hasConsistentBottomBorder || isSticky },
        { 'bg-black': !isTransparent || isSticky },
        'sticky px-2 md:px-4 top-[-1px] -bottom-9 w-full flex z-[40] h-[var(--secondaryNavHeight)]',
        'before:content-[""] before:absolute before:inset-0  before:border-defaultBorder'
      )}
    >
      <NavigationTabs
        ref={navRef}
        className="flex flex-1 items-center"
        title={ARCADE_LINKS.find(link => link.path === pathname)?.label}
        subtitle={subtitle}
        links={arcadeLinks}
      />
      {canUpdateArcadeSeason && (
        <Button
          theme="clean"
          onClick={() =>
            showModal(MODAL_ID.devOnly.arcadeSettings, {
              season
            })
          }
          className="relative z-10 ml-1 p-2"
        >
          <Icon name="settings1" className="w-3 h-3" />
        </Button>
      )}
    </div>
  )
}

export default ArcadeNavigation
